import React, { useState } from 'react';
import { Tabs, Table, Spin, Tag, Descriptions, Button, Form, Space, InputNumber, DatePicker, Input, Alert } from 'antd';
import type { TabsProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileSearchOutlined, ScheduleOutlined } from '@ant-design/icons';
import axios from 'axios';
import SweetAlert from 'sweetalert2';
import { useAuthHeader, useAuthUser } from '../../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../../Models/Utilisateur';
import Data from '../../../../Data/Data.json';
import { Crypter } from '../../../../Functions/Security/Crypter';
import { Box, Typography } from '@mui/material';
import SearchInput from '../../../SearchInput';
import SearchModal from '../../../SearchModal';
import Params from '../../../../Types/ParamsPicklist';
import formatNumber from '../../../../Functions/Traitement/FormatNumber';
import { useTypePret, termeBounds, ruleDuree, ruleMaxValue, toNumField, PERIOD_MONTHS } from '../../../TypePretInfoModal';
import { GlobalExportToPDF } from '../../../../utilitaires/globalFunction';
import dayjs from 'dayjs';

const ReechelonnementPret = () => {
    const auth = useAuthUser();
    const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
    const authHeader = useAuthHeader();

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [pret, setPret] = useState<any>(null);
    const [echeances, setEcheances] = useState<any[]>([]);
    const [newEcheances, setNewEcheances] = useState<any[]>([]);
    const [simus, setSimus] = useState<any[]>([]);
    const [simuSel, setSimuSel] = useState<React.Key | null>(null);
    const [simuInfo, setSimuInfo] = useState<any>(null);
    const { typePret, loadTypePret, typePretModal } = useTypePret();

    const [simulActive, setSimulActive] = useState(true);
    const [annulActive, setAnnulActive] = useState(true);
    const [actuActive, setActActive] = useState(true);

    let searchPret: Params = { CodeAgence: '', Table: "PRETS", Colonne1: "NUMDOS", Colonne2: "NOMCLI", Colagence: '101', Choix: 22 };
    let searchPeriod: Params = { CodeAgence: '', Table: "v_period_lib", Colonne1: "CODLIB", Colonne2: "LIBELL", Colagence: '101', Choix: 22 };
    let searchMotif: Params = { CodeAgence: '', Table: "v_motif_reech_simu", Colonne1: "CODMOTIF", Colonne2: "LIBMOTIF", Colagence: '101', Choix: 22 };

    // ---------------- Appels API ----------------
    function callApi(action: string, extra: any = {}) {
        setLoading(true);
        const d = form.getFieldValue('DAT1ECHE_NEW');
        const payload = Crypter.encrypt(JSON.stringify({
            action,
            numDos: form.getFieldValue('NUMDOS'),
            idReech: action === 'SIMU' && simuSel ? simuSel : null,
            nbrEche: form.getFieldValue('NBRECHE_NEW'),
            periodic: form.getFieldValue('PERIODIC_NEW'),
            dat1Eche: dayjs.isDayjs(d) ? d.format('YYYY-MM-DD') : d,
            motif: form.getFieldValue('MOTIF'),
            coment: form.getFieldValue('COMMENTAIRE'),
            utiVal: user.coduti,
            codAge: user.codage,
            ...extra
        }));
        axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.ReschedulePret,
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' },
            data: payload
        })
            .then((response) => {
                setLoading(false);
                if (!response.data) return;
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode == 200) {
                    SweetAlert.fire({ title: res.Message, icon: "success" });
                    if (action === 'SIMU' && res.Result) {
                        // La table des échéances est celle qui contient la colonne numSeq
                        const sched = [res.Result.Table, res.Result.Table1, res.Result.Table2]
                            .find((t: any[]) => Array.isArray(t) && t.length > 0 && t[0].numSeq !== undefined);
                        if (sched) setNewEcheances(sched);
                        // Rafraichit la liste des simulations enregistrees
                        loadSimus(form.getFieldValue('NUMDOS'));
                    }
                    if (action === 'ANNULE') {
                        setNewEcheances([]);
                    }
                } else {
                    SweetAlert.fire({ title: res.Message, icon: "warning" });
                }
            })
            .catch((error) => {
                setLoading(false);
                SweetAlert.fire({ title: String(error), icon: "error" });
            });
    }

    // ---------------- Simulations enregistrees du dossier ----------------
    function loadSimus(numdos: string) {
        setLoading(true);
        axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.ReschedulePret,
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' },
            data: Crypter.encrypt(JSON.stringify({
                action: 'LISTESIMU', numDos: numdos, utiVal: user.coduti, codAge: user.codage
            }))
        })
            .then((response) => {
                setLoading(false);
                if (!response.data) return;
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                const list = res.Result?.Table ?? [];
                setSimus(list);
                // Charge automatiquement la derniere simulation (form + echeancier)
                if (list.length > 0) {
                    setSimuSel(list[0].Id);
                    loadSimuDetail(list[0].Id);
                }
            })
            .catch(() => setLoading(false));
    }

    function loadSimuDetail(id: React.Key) {
        setLoading(true);
        axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.ReschedulePret,
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' },
            data: Crypter.encrypt(JSON.stringify({
                action: 'DETAIL', idReech: id, utiVal: user.coduti, codAge: user.codage
            }))
        })
            .then((response) => {
                setLoading(false);
                if (!response.data) return;
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode == 200) {
                    const d = res.Result?.Table?.[0];
                    const ech = res.Result?.Table1 ?? [];
                    // Remplit le formulaire avec les parametres de la simulation
                    if (d) {
                        setSimuInfo(d);
                        form.setFieldsValue({
                            NBRECHE_NEW: d.NBRECHE,
                            PERIODIC_NEW: d.PERIODIC,
                            lib_PERIODIC_NEW: d.PERIODIC,
                            DAT1ECHE_NEW: d.DAT1ECHE ? dayjs(String(d.DAT1ECHE).substring(0, 10)) : null,
                            MOTIF: d.MOTIF,
                            lib_MOTIF: d.MOTIF,
                            COMMENTAIRE: d.COMMENTAIRE
                        });
                    }
                    // Affiche l'echeancier de la simulation dans le bloc previsionnel
                    setNewEcheances(ech);
                }
            })
            .catch(() => setLoading(false));
    }

    // ---------------- Preview du nouveau capital (non persiste) ----------------
    function previewCapital(thenConfirm: () => void) {
        setLoading(true);
        axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.ReschedulePret,
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' },
            data: Crypter.encrypt(JSON.stringify({
                action: 'CAPITAL', numDos: form.getFieldValue('NUMDOS'),
                utiVal: user.coduti, codAge: user.codage
            }))
        })
            .then((response) => {
                setLoading(false);
                if (!response.data) return;
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode != 200) { SweetAlert.fire({ title: res.Message, icon: "warning" }); return; }
                const c = res.Result?.Table?.[0];
                SweetAlert.fire({
                    title: simuSel ? `Modifier la simulation ${simuSel} ?` : "Confirmer la simulation ?",
                    html: `<table style="margin:auto;text-align:left;font-size:14px">
                        <tr><td>Solde capital restant</td><td style="text-align:right"><b>${formatNumber(c?.soldeCapital ?? 0)}</b></td></tr>
                        <tr><td>+ Intérêts impayés capitalisés</td><td style="text-align:right"><b>${formatNumber(c?.interetsCapitalises ?? 0)}</b></td></tr>
                        <tr><td>+ Pénalités capitalisées</td><td style="text-align:right"><b>${formatNumber(c?.penalitesCapitalisees ?? 0)}</b></td></tr>
                        <tr style="border-top:1px solid #116599"><td><b>= Nouveau capital</b></td><td style="text-align:right;color:#116599"><b>${formatNumber(c?.nouveauCapital ?? 0)}</b></td></tr>
                        <tr><td>Frais de rééchelonnement${c?.codOpe ? ` (${c.codOpe})` : ''}</td><td style="text-align:right"><b>${formatNumber(c?.frais ?? 0)}</b></td></tr>
                    </table>`,
                    showCancelButton: true, confirmButtonText: simuSel ? "Modifier" : "Simuler", cancelButtonText: "Annuler",
                    confirmButtonColor: "#116599", icon: 'question', width: 480
                }).then((r) => { if (r.isConfirmed) thenConfirm(); });
            })
            .catch(() => setLoading(false));
    }

    // ---------------- Consultation du pret ----------------
    function consult() {
        const numdos = form.getFieldValue('NUMDOS');
        if (!numdos) { SweetAlert.fire({ title: "Sélectionnez un dossier de prêt", icon: "warning" }); return; }
        setLoading(true);
        axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.GetPretConsult,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
                'CipherNumDos': Crypter.encrypt(numdos)
            }
        })
            .then((response) => {
                setLoading(false);
                if (!response.data) return;
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode == 200) {
                    const r = res.Result;
                    setPret(r.Table?.[0] ?? null);
                    setEcheances(r.Table1 ?? []);
                    // Charge le type de pret (+ TERME) pour les validations des champs
                    loadTypePret(r.Table?.[0]?.TYPEPRET);
                    setSimulActive(false);
                    setNewEcheances([]);
                    setSimuSel(null);
                    loadSimus(numdos);
                    setAnnulActive(false);
                    setActActive(false);
                } else {
                    SweetAlert.fire({ title: res.Message, icon: "warning" });
                }
            })
            .catch((error) => {
                setLoading(false);
                SweetAlert.fire({ title: String(error), icon: "error" });
            });
    }

    function handleAnnul() {
        form.resetFields();
        setPret(null);
        setEcheances([]);
        setNewEcheances([]);
        setSimus([]);
        setSimuSel(null);
        setSimuInfo(null);
        setSimulActive(true);
        setAnnulActive(true);
        setActActive(true);
    }

    // ---------------- Tables ----------------
    const colsSimu: ColumnsType<any> = [
        { title: 'N°', dataIndex: 'numSeq' },
        { title: 'Date', dataIndex: 'dateEcheacnce' },
        { title: 'Capital', dataIndex: 'capital', render: (v: number) => formatNumber(v) },
        { title: 'Amort.', dataIndex: 'amortissement', render: (v: number) => formatNumber(v) },
        { title: 'Intérêt', dataIndex: 'interet', render: (v: number) => formatNumber(v) },
        { title: 'Taxe', dataIndex: 'taxe', render: (v: number) => formatNumber(v) },
        { title: 'Épargne', dataIndex: 'epargne', render: (v: number) => formatNumber(v) },
        { title: 'Annuité TTC', dataIndex: 'anTTC', render: (v: number) => formatNumber(v) },
    ];

    const tagEtat = (etat: string) => {
        switch (etat) {
            case 'Payée': return <Tag color="green">Payée</Tag>;
            case 'Impayée': return <Tag color="red">Impayée</Tag>;
            case 'Douteux': return <Tag color="volcano">Douteux</Tag>;
            case 'Prévisionnel': return <Tag color="default">Prévisionnel</Tag>;
            default: return <Tag color="blue">À échoir</Tag>;
        }
    };

    const colsEcp: ColumnsType<any> = [
        { title: 'N°', dataIndex: 'NUMSEQ' },
        { title: 'Date', dataIndex: 'DATECH', render: (v: string) => v ? String(v).substring(0, 10) : '' },
        { title: 'Amort.', dataIndex: 'AMMORT', render: (v: number) => formatNumber(v) },
        { title: 'Intérêt', dataIndex: 'INTERE', render: (v: number) => formatNumber(v) },
        { title: 'Taxe', dataIndex: 'TAXE', render: (v: number) => formatNumber(v) },
        { title: 'Épargne', dataIndex: 'EPARGNE', render: (v: number) => formatNumber(v) },
        { title: 'Annuité', dataIndex: 'ANNUIT', render: (v: number) => formatNumber(v) },
        { title: 'Montant CRD', dataIndex: 'MTNCRD', render: (v: number) => formatNumber(v) },
        { title: 'Statut', dataIndex: 'LIBETATECH', render: (v: string) => tagEtat(v) },
    ];

    const colsSimuList: ColumnsType<any> = [
        { title: 'ID', dataIndex: 'Id', width: 60 },
        { title: 'Échéances', dataIndex: 'NBRECHE' },
        { title: 'Périod.', dataIndex: 'PERIODIC' },
        { title: '1ère éch.', dataIndex: 'DAT1ECHE', render: (v: string) => v ? String(v).substring(0, 10) : '' },
        { title: 'Nouv. capital', dataIndex: 'MNTCAPIT', render: (v: number) => formatNumber(v) },
        { title: 'Frais', dataIndex: 'MTNFRAIS', render: (v: number) => formatNumber(v) },
        { title: 'Version', dataIndex: 'VERSIONECP' },
        { title: 'Par', dataIndex: 'UTILIS' },
        { title: 'Le', dataIndex: 'DATCRE', render: (v: string) => v ? String(v).substring(0, 16).replace('T', ' ') : '' },
        { title: 'Motif', dataIndex: 'MOTIF', ellipsis: true },
    ];

    const tabsItems: TabsProps['items'] = [
        {
            key: '1',
            label: <span><FileSearchOutlined /> Prêt concerné</span>,
            children: (
                <div className='tabItem' style={{ display: 'block', width: '100%' }}>
                    <fieldset className="bordure1" style={{ marginBottom: 12, width: '100%' }}>
                        <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Prêt à rééchelonner</legend>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '35% minmax(0, 1fr)', gap: 1, alignItems: 'center' }}>
                            <Box sx={{ textAlign: 'left', pr: 1 }}>
                                <span style={{ color: 'red', marginRight: 4 }}>*</span>Numéro dossier
                            </Box>
                            <Box sx={{ minWidth: 0, width: '100%' }}>
                                <SearchInput width={0} form={form} Titre='Recherche dossier' label='' name='NUMDOS' Parametre={searchPret} />
                            </Box>
                        </Box>
                    </fieldset>
                    {pret && (
                        <Descriptions size="small" column={2} bordered style={{ marginTop: 12 }}>
                            <Descriptions.Item label="Client">{pret.NOMCLI} ({pret.NUMCLI})</Descriptions.Item>
                            <Descriptions.Item label="Type de prêt">{pret.TYPEPRET}</Descriptions.Item>
                            <Descriptions.Item label="Montant initial">{formatNumber(pret.MONTAN)}</Descriptions.Item>
                            <Descriptions.Item label="Taux intérêt">{pret.TAUXINT}%</Descriptions.Item>
                            <Descriptions.Item label="Échéances">{pret.NBRECHE}</Descriptions.Item>
                            <Descriptions.Item label="Périodicité">{pret.PERIODIC}</Descriptions.Item>
                            <Descriptions.Item label="Solde capital restant">{formatNumber(pret.RESTANTDU ?? 0)}</Descriptions.Item>
                            <Descriptions.Item label="Échéances impayées">{pret.NBECHIMP ?? 0}</Descriptions.Item>
                            <Descriptions.Item label="Statut">{pret.LIBETAT}</Descriptions.Item>
                            <Descriptions.Item label="Prochaine échéance">{pret.PROCHAINEECH ? String(pret.PROCHAINEECH).substring(0, 10) : '—'}</Descriptions.Item>
                            <Descriptions.Item label="Date validation">{pret.DateVal ? String(pret.DateVal).substring(0, 10) : '—'}</Descriptions.Item>
                            <Descriptions.Item label="Validateur">{pret.UTIVAL ?? '—'}</Descriptions.Item>
                            <Descriptions.Item label="Gestionnaire">{pret.LIBCODGES || pret.CODGES || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Éch. payées">{`${pret.NBECHPAYE ?? 0} — ${formatNumber(pret.MTNECHPAYE ?? 0)}`}</Descriptions.Item>
                            <Descriptions.Item label="Éch. non payées">{`${(pret.NBECHTOT ?? 0) - (pret.NBECHPAYE ?? 0)} — ${formatNumber(pret.MTNECHIMPAYE ?? 0)}`}</Descriptions.Item>
                        </Descriptions>
                    )}
                    {pret && (
                        <fieldset className="bordure1" style={{ marginTop: 12, width: '100%' }}>
                            <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Tableau d'amortissement actuel</legend>
                            {echeances.length === 0 ? (
                                <Typography variant="caption" color="textSecondary">
                                    Aucun échéancier pour ce dossier.
                                </Typography>
                            ) : (
                                <Table size='small' columns={colsEcp} dataSource={echeances}
                                    scroll={{ x: 700, y: 250 }} pagination={false} rowKey="NUMSEQ" />
                            )}
                        </fieldset>
                    )}
                </div>
            )
        },
        {
            key: '2',
            label: <span><ScheduleOutlined /> Nouveau calendrier</span>,
            children: (
                <div className='tabItem' style={{ display: 'block', width: '100%' }}>
                    <fieldset className="bordure1" style={{ marginBottom: 12, width: '100%' }}>
                        <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Paramètres du nouveau calendrier</legend>

                        {pret && typePret && (
                            <Alert
                                type={typePret.isReechelonnable ? 'info' : 'error'}
                                showIcon
                                style={{ marginBottom: 12 }}
                                message={typePret.isReechelonnable
                                    ? `Conditions du type de prêt ${typePret.TYPEPRET} : max ${typePret.nbrReechMax ?? '∞'} rééchelonnement(s), délai min. ${typePret.delaiMinReech ?? 0} mois, min. ${typePret.nbMinEchPayees ?? 0} échéance(s) payée(s)` +
                                      (typePret._TERME ? `, durée ${typePret._TERME.DUREEMIN}–${typePret._TERME.DUREEMAX || '∞'} mois, max ${typePret._TERME.NBRECHMAX} échéances` : '') +
                                      (typePret.isCapitaliserInt ? ', intérêts capitalisés' : '') +
                                      (typePret.isCapitaliserPen ? ', pénalités capitalisées' : '') +
                                      (typePret.isReechValide ? ', validation requise' : '')
                                    : `Le type de prêt ${typePret.TYPEPRET} n'autorise pas le rééchelonnement`}
                            />
                        )}

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
                            <Form.Item name="NBRECHE_NEW" label="Nouveau nombre d'échéances"
                                rules={[
                                    { required: true, message: 'Obligatoire' },
                                    typePret?._TERME
                                        ? ruleDuree('', () => form.getFieldValue('PERIODIC_NEW'), toNumField, termeBounds(typePret._TERME))
                                        : { validator: () => Promise.resolve() },
                                    ...(typePret?._TERME?.NBRECHMAX
                                        ? [ruleMaxValue(Number(typePret._TERME.NBRECHMAX), "Nombre d'échéances")]
                                        : [])
                                ]}>
                                <InputNumber style={{ width: '100%' }} min={1} />
                            </Form.Item>
                            <SearchModal width={9} form={form} Titre="Recherche Périodicité" label='Périodicité' name='PERIODIC_NEW' Parametre={searchPeriod} />
                            <Form.Item name="DAT1ECHE_NEW" label="Date 1ère échéance"
                                rules={[
                                    { required: true, message: 'Obligatoire' },
                                    {
                                        validator: (_: any, v: any) => {
                                            if (!v) return Promise.resolve();
                                            const d = dayjs.isDayjs(v) ? v : dayjs(v);
                                            return d.isAfter(dayjs())
                                                ? Promise.resolve()
                                                : Promise.reject(new Error("La 1ère échéance doit être postérieure à aujourd'hui"));
                                        }
                                    }
                                ]}>
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>
                            <SearchModal width={9} form={form} Titre='Recherche motif' label='Motif du rééchelonnement' name='MOTIF' Parametre={searchMotif} />
                            <Form.Item name="COMMENTAIRE" label="Commentaire">
                                <Input />
                            </Form.Item>
                        </Box>
                    </fieldset>

                    {simuInfo && (
                        <fieldset className="bordure1" style={{ marginTop: 12, width: '100%' }}>
                            <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Composition du nouveau capital — simulation {String(simuInfo.Id)}</legend>
                            <Descriptions size="small" column={2} bordered>
                                <Descriptions.Item label="Solde capital restant">{formatNumber((simuInfo.MNTCAPIT ?? 0) - (simuInfo.INTIMPAYE ?? 0) - (simuInfo.PENIMPAYE ?? 0))}</Descriptions.Item>
                                <Descriptions.Item label="Intérêts impayés capitalisés">{formatNumber(simuInfo.INTIMPAYE ?? 0)}</Descriptions.Item>
                                <Descriptions.Item label="Pénalités capitalisées">{formatNumber(simuInfo.PENIMPAYE ?? 0)}</Descriptions.Item>
                                <Descriptions.Item label="Nouveau capital"><b>{formatNumber(simuInfo.MNTCAPIT ?? 0)}</b></Descriptions.Item>
                                <Descriptions.Item label="Frais de rééchelonnement">{formatNumber(simuInfo.MTNFRAIS ?? 0)}</Descriptions.Item>
                                <Descriptions.Item label="Motif">{simuInfo.MOTIF}{simuInfo.COMMENTAIRE ? ' — ' + simuInfo.COMMENTAIRE : ''}</Descriptions.Item>
                            </Descriptions>
                        </fieldset>
                    )}

                    {pret && simus.length > 0 && (
                        <fieldset className="bordure1" style={{ marginTop: 12, width: '100%' }}>
                            <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Simulations enregistrées ({simus.length}) — sélectionnez pour charger</legend>
                            {simuSel !== null && (
                                <Button size='small' className='bt_add' style={{ marginBottom: 6 }}
                                    onClick={() => { setSimuSel(null); setNewEcheances([]); }}
                                    type="ghost" htmlType="button">+ Nouvelle simulation (vider la sélection)</Button>
                            )}
                            <Table size='small' columns={colsSimuList} dataSource={simus}
                                scroll={{ x: 900, y: 160 }} pagination={false} rowKey="Id"
                                rowSelection={{
                                    type: 'radio',
                                    selectedRowKeys: simuSel ? [simuSel] : [],
                                    onChange: (keys) => {
                                        const k = keys.length ? keys[0] : null;
                                        setSimuSel(k);
                                        if (k) loadSimuDetail(k); else setNewEcheances([]);
                                    }
                                }} />
                        </fieldset>
                    )}

                    <fieldset className="bordure1" style={{ marginTop: 12, width: '100%' }}>
                        <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Échéancier prévisionnel (simulation)</legend>
                        {newEcheances.length === 0 ? (
                            <Typography variant="caption" color="textSecondary">
                                Lancez une simulation pour visualiser le nouvel échéancier prévisionnel.
                            </Typography>
                        ) : (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Tag color="orange">
                                        {simuSel ? `Échéancier de la simulation ${String(simuSel)}` : "Échéancier prévisionnel"} — version REECH
                                    </Tag>
                                    <Button size='small' className='bt_add'
                                        onClick={() => GlobalExportToPDF(
                                            newEcheances.map(e => ({
                                                'N°': e.numSeq,
                                                'Date': e.dateEcheacnce,
                                                'Capital': e.capital,
                                                'Amortissement': e.amortissement,
                                                'Intérêt': e.interet,
                                                'Taxe': e.taxe,
                                                'Épargne': e.epargne,
                                                'Annuité HT': e.anHT,
                                                'Annuité TTC': e.anTTC
                                            })),
                                            `Échéancier rééchelonnement — dossier ${form.getFieldValue('NUMDOS')}`,
                                            `Echeancier_Reech_${form.getFieldValue('NUMDOS')}`
                                        )}
                                        type="ghost" htmlType="button">Exporter PDF</Button>
                                </Box>
                                <Table size='small' columns={colsSimu} dataSource={newEcheances}
                                    scroll={{ x: 700, y: 260 }} pagination={false} rowKey="numSeq" />
                            </>
                        )}
                    </fieldset>
                </div>
            )
        }
    ];

    return (
        <div className='tab'>
            <Typography align='center' className='formTitle'><b>Simulation de rééchelonnement</b></Typography>
            <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
                <Form
                    form={form}
                    layout="vertical"
                    labelAlign='left'
                    onFinish={() => { }}
                    onFinishFailed={() => { }}
                >
                    <div className='bG'>
                        <legend><Typography color={"#116599"}>Actions</Typography></legend>
                        <Form.Item shouldUpdate className='bouttong'>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button size='large' className='bt_add' onClick={consult} type="ghost" htmlType="button" block>Consulter</Button>
                                <Button size='large' className='bt_add' disabled={simulActive} onClick={() => {
                                    if (typePret && !typePret.isReechelonnable) {
                                        SweetAlert.fire({ title: `Le type de prêt ${typePret.TYPEPRET} n'autorise pas le rééchelonnement`, icon: "error" });
                                        return;
                                    }
                                    form.validateFields(['NBRECHE_NEW', 'PERIODIC_NEW', 'DAT1ECHE_NEW', 'MOTIF'])
                                        .then(() => previewCapital(() => callApi('SIMU')))
                                        .catch(() => SweetAlert.fire({ title: "Vérifiez les champs du nouveau calendrier", icon: "warning" }));
                                }} type="ghost" htmlType="button" block>{simuSel ? 'Modifier la simulation' : 'Simuler'}</Button>
                                <Button size='large' className='bt_add' disabled={actuActive} onClick={() => form.resetFields()} type="ghost" htmlType="button" block>Actualiser</Button>
                                <Button size='large' className='bt_add' disabled={annulActive} onClick={handleAnnul} type="ghost" htmlType="reset" block>Annuler</Button>
                            </Space>
                        </Form.Item>
                    </div>
                    <div className='clientP'>
                        <div style={{ width: '88%' }}>
                            <Tabs size='small' type="card" defaultActiveKey="1" tabBarGutter={8}
                                tabBarStyle={{ overflowX: 'auto', whiteSpace: 'nowrap' }} items={tabsItems} />
                        </div>
                    </div>
                </Form>
                {typePretModal}
            </Spin>
        </div>
    );
};

export default ReechelonnementPret;
